import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import SectionHeading from '../../components/common/SectionHeading';
import { getNoticeAuthorDisplay } from '../../utils/noticeAuthor';

const PORTAL_USER_KEY = 'portal_user';
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5003';
const HOME_NOTICES_PAGE_SIZE = 5;
const HOME_BOARD_PAGE_SIZE = 5;

const Wrapper = styled.section`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 1.5rem;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const MessageSection = styled.section`
  margin-top: 1rem;
  padding: 1rem 1.2rem;
  border-radius: 1rem;
  border: 1px solid rgba(99, 102, 241, 0.25);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.12), rgba(15, 23, 42, 0.05));
`;

const MessageHeading = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
`;

const MessagePrefix = styled.p`
  margin: 0.45rem 0 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1rem;
  line-height: 1.55;
  font-weight: 700;
`;

const RandomQuote = styled.p`
  margin: 0.2rem 0 0;
  color: ${({ theme }) => theme.colors.text};
  font-size: 1.03rem;
  line-height: 1.65;
  font-weight: 500;
`;

const MessageLoadingLine = styled.p`
  margin: 0.45rem 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.98rem;
  line-height: 1.55;
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const MessageErrorLine = styled.p`
  margin: 0.45rem 0 0;
  color: #b45309;
  font-size: 0.92rem;
  line-height: 1.5;
`;

const Grid = styled.div`
  margin-top: 2rem;
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const MainGrid = styled(Grid)`
  margin-top: 1rem;
`;

const HomePanel = styled.div`
  display: block;
  border: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  border-radius: 1rem;
  padding: 1.2rem;
  background: ${({ theme }) => theme.colors.surface};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: rgba(99, 102, 241, 0.28);
    box-shadow: 0 2px 12px rgba(15, 23, 42, 0.06);
  }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.45rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
`;

const SectionDescription = styled.p`
  margin: 0 0 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.6;
  font-size: 0.9rem;
`;

const PanelFootLink = styled(Link)`
  display: inline-block;
  margin-top: 0.75rem;
  font-size: 0.82rem;
  font-weight: 700;
  color: #4f46e5;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const NoticeList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
`;

const NoticeItem = styled.li`
  border-bottom: 1px solid ${({ theme }) => theme.colors.surfaceMuted};
  &:last-child {
    border-bottom: none;
  }
`;

const NoticeLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.65rem;
  padding: 0.65rem 0;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  &:hover .notice-title {
    color: #4f46e5;
  }
`;

const NoticeTitleRow = styled.span`
  display: flex;
  align-items: flex-start;
  gap: 0.35rem;
  min-width: 0;
  flex: 1;
`;

const NoticePin = styled.span`
  flex-shrink: 0;
  font-size: 0.85rem;
  line-height: 1.45;
`;

const NoticeTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.45;
  word-break: break-word;
`;

const NoticeMeta = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  color: #94a3b8;
  white-space: nowrap;
  padding-top: 0.12rem;
`;

const BoardRowLink = styled(Link)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0;
  text-decoration: none;
  color: inherit;
  min-width: 0;
  &:hover .board-post-title {
    color: #4f46e5;
  }
`;

const BoardPostLeft = styled.span`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.3rem 0.45rem;
  min-width: 0;
  flex: 1;
`;

const BoardPostTitle = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.45;
  word-break: break-word;
`;

const BoardBracketCount = styled.span`
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #64748b;
`;

const BoardLikeCount = styled.span`
  flex-shrink: 0;
  font-size: 0.78rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #94a3b8;
`;

const BoardPostAuthor = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  max-width: 42%;
  min-width: 0;
`;

const BoardAuthorEmoji = styled.span`
  flex-shrink: 0;
  font-size: 0.95rem;
  line-height: 1;
`;

const BoardAuthorName = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const NoticeMuted = styled.p`
  margin: 0.5rem 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.88rem;
`;

const PresentationQuickLink = styled(Link)`
  display: block;
  margin-top: 1rem;
  padding: 1.15rem 1.25rem;
  border: 1px solid rgba(99, 102, 241, 0.35);
  border-radius: 1rem;
  background: linear-gradient(135deg, #eef2ff, #e0e7ff 55%, #dbeafe);
  text-decoration: none;
  color: #312e81;
  font-size: 0.92rem;
  font-weight: 700;
  box-shadow: 0 2px 10px rgba(79, 70, 229, 0.12);

  &:hover {
    background: linear-gradient(135deg, #e8edff, #dbe5ff 55%, #d2e6ff);
  }
`;

const BannerEyebrow = styled.span`
  display: inline-block;
  font-size: 0.74rem;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #dc2626;
  opacity: 0.9;
  font-weight: 800;
`;

const BannerTitle = styled.p`
  margin: 0.35rem 0 0;
  font-size: 1.02rem;
  font-weight: 800;
  color: #312e81;
`;

const BannerDesc = styled.p`
  margin: 0.3rem 0 0;
  font-size: 0.84rem;
  line-height: 1.5;
  color: #475569;
`;

const readPortalUser = () => {
  try {
    const raw = sessionStorage.getItem(PORTAL_USER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.roles) ? parsed : null;
  } catch {
    return null;
  }
};

const formatNoticeDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' });
};

const PortalHomePage = () => {
  const portalUser = readPortalUser();
  const [dailyMessage, setDailyMessage] = useState('');
  const [teacherDaysWithAce, setTeacherDaysWithAce] = useState(null);
  const [dailyLoadState, setDailyLoadState] = useState('idle');
  const [homeNotices, setHomeNotices] = useState([]);
  const [noticesLoadState, setNoticesLoadState] = useState('idle');
  const [homeBoardPosts, setHomeBoardPosts] = useState([]);
  const [boardLoadState, setBoardLoadState] = useState('idle');

  useEffect(() => {
    if (!portalUser?.login_id) {
      setDailyMessage('');
      setTeacherDaysWithAce(null);
      setDailyLoadState('idle');
      setHomeNotices([]);
      setNoticesLoadState('idle');
      setHomeBoardPosts([]);
      setBoardLoadState('idle');
      return;
    }

    let cancelled = false;
    const login = encodeURIComponent(portalUser.login_id);
    const dailyUrl = `${BACKEND_BASE_URL}/api/auth/daily-message?login_id=${login}`;
    const noticesUrl = `${BACKEND_BASE_URL}/api/notices?login_id=${login}&page=1&page_size=${HOME_NOTICES_PAGE_SIZE}`;
    const boardUrl = `${BACKEND_BASE_URL}/api/student-board/posts?login_id=${login}&page=1&page_size=${HOME_BOARD_PAGE_SIZE}`;

    setDailyLoadState('loading');
    setNoticesLoadState('loading');
    setBoardLoadState('loading');

    const parseJson = async (res) => {
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const err = new Error('request failed');
        err.responseData = data;
        throw err;
      }
      return data;
    };

    Promise.allSettled([
      fetch(dailyUrl, { method: 'GET' }).then(parseJson),
      fetch(noticesUrl, { method: 'GET' }).then(parseJson),
      fetch(boardUrl, { method: 'GET' }).then(parseJson),
    ])
      .then(([dailyResult, noticesResult, boardResult]) => {
        if (cancelled) return;

        if (dailyResult.status === 'fulfilled') {
          const data = dailyResult.value;
          setDailyMessage(data?.message || '');
          setTeacherDaysWithAce(
            Number.isInteger(data?.teacher_days_with_ace) ? data.teacher_days_with_ace : null,
          );
          setDailyLoadState('ready');
        } else {
          setDailyMessage('');
          setTeacherDaysWithAce(null);
          setDailyLoadState('error');
        }

        if (noticesResult.status === 'fulfilled') {
          const list = noticesResult.value?.notices;
          setHomeNotices(Array.isArray(list) ? list : []);
          setNoticesLoadState('ready');
        } else {
          setHomeNotices([]);
          setNoticesLoadState('error');
        }

        if (boardResult.status === 'fulfilled') {
          const list = boardResult.value?.posts;
          setHomeBoardPosts(Array.isArray(list) ? list : []);
          setBoardLoadState('ready');
        } else {
          setHomeBoardPosts([]);
          setBoardLoadState('error');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setDailyMessage('');
        setTeacherDaysWithAce(null);
        setDailyLoadState('error');
        setHomeNotices([]);
        setNoticesLoadState('error');
        setHomeBoardPosts([]);
        setBoardLoadState('error');
      });

    return () => {
      cancelled = true;
    };
  }, [portalUser?.login_id]);

  const showMessageBlock = Boolean(portalUser?.login_id);

  return (
    <Wrapper>
      <SectionHeading
        eyebrow="PORTAL HOME"
        title="포털 홈"
        description="오늘의 인사와 바로 가기 메뉴로 필요한 화면으로 이동해 보세요."
      />
      {showMessageBlock ? (
        <MessageSection>
          <MessageHeading>Today's Message</MessageHeading>
          {dailyLoadState === 'loading' ? (
            <MessageLoadingLine>메세지 로딩중 ···</MessageLoadingLine>
          ) : null}
          {dailyLoadState === 'error' ? (
            <MessageErrorLine>메시지를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.</MessageErrorLine>
          ) : null}
          {dailyLoadState === 'ready' ? (
            <>
              {teacherDaysWithAce ? (
                <MessagePrefix>에이스코딩과 함께한 지 {teacherDaysWithAce}일째 💗</MessagePrefix>
              ) : null}
              {dailyMessage ? <RandomQuote>{dailyMessage}</RandomQuote> : null}
            </>
          ) : null}
        </MessageSection>
      ) : null}
      <PresentationQuickLink to="/portal/presentations/year/2026">
        <BannerEyebrow>🔥 HOT</BannerEyebrow>
        <BannerTitle>2026 어린이날 발표회 바로가기 →</BannerTitle>
        <BannerDesc>학생 작품 영상과 좋아요, 댓글을 확인해 보세요.</BannerDesc>
      </PresentationQuickLink>
      <MainGrid>
        <HomePanel>
          <PanelHead>
            <SectionTitle>공지사항</SectionTitle>
          </PanelHead>
          <SectionDescription>학원에서 전하는 소식과 안내를 확인해 보세요.</SectionDescription>
          {noticesLoadState === 'loading' ? (
            <NoticeMuted>공지 목록 불러오는 중 ···</NoticeMuted>
          ) : null}
          {noticesLoadState === 'error' ? (
            <NoticeMuted>공지를 불러오지 못했어요.</NoticeMuted>
          ) : null}
          {noticesLoadState === 'ready' && homeNotices.length === 0 ? (
            <NoticeMuted>등록된 공지가 없어요.</NoticeMuted>
          ) : null}
          {noticesLoadState === 'ready' && homeNotices.length > 0 ? (
            <NoticeList>
              {homeNotices.map((n) => (
                <NoticeItem key={n.id}>
                  <NoticeLink to={`/portal/notices/${n.id}`}>
                    <NoticeTitleRow>
                      {n.is_pinned ? (
                        <NoticePin title="상단 고정" aria-hidden>
                          📌
                        </NoticePin>
                      ) : null}
                      <NoticeTitle className="notice-title">{n.title || '(제목 없음)'}</NoticeTitle>
                    </NoticeTitleRow>
                    <NoticeMeta>{formatNoticeDate(n.created_at)}</NoticeMeta>
                  </NoticeLink>
                </NoticeItem>
              ))}
            </NoticeList>
          ) : null}
          <PanelFootLink to="/portal/notices">공지 전체 보기 →</PanelFootLink>
        </HomePanel>
        <HomePanel>
          <PanelHead>
            <SectionTitle>학생 자유 게시판</SectionTitle>
          </PanelHead>
          <SectionDescription>코딩학원 친구들, 선생님과 소통해 보세요.</SectionDescription>
          {boardLoadState === 'loading' ? (
            <NoticeMuted>게시글 불러오는 중 ···</NoticeMuted>
          ) : null}
          {boardLoadState === 'error' ? (
            <NoticeMuted>게시글을 불러오지 못했어요.</NoticeMuted>
          ) : null}
          {boardLoadState === 'ready' && homeBoardPosts.length === 0 ? (
            <NoticeMuted>등록된 게시글이 없어요.</NoticeMuted>
          ) : null}
          {boardLoadState === 'ready' && homeBoardPosts.length > 0 ? (
            <NoticeList>
              {homeBoardPosts.map((p) => {
                const { primary } = getNoticeAuthorDisplay(p.author);
                const n = Number(p.comment_count);
                const cc = Number.isFinite(n) ? n : 0;
                const lcRaw = Number(p.like_count);
                const lc = Number.isFinite(lcRaw) ? lcRaw : 0;
                return (
                  <NoticeItem key={p.id}>
                    <BoardRowLink to={`/portal/board/${p.id}`}>
                      <BoardPostLeft>
                        <BoardPostTitle className="board-post-title">{p.title || '(제목 없음)'}</BoardPostTitle>
                        <BoardBracketCount title="댓글 수">[{cc}]</BoardBracketCount>
                        <BoardLikeCount title="좋아요">♥ {lc}</BoardLikeCount>
                      </BoardPostLeft>
                      <BoardPostAuthor title={primary}>
                        <BoardAuthorEmoji aria-hidden>{p.author?.profile_emoji || '🙂'}</BoardAuthorEmoji>
                        <BoardAuthorName>{primary}</BoardAuthorName>
                      </BoardPostAuthor>
                    </BoardRowLink>
                  </NoticeItem>
                );
              })}
            </NoticeList>
          ) : null}
          <PanelFootLink to="/portal/board">게시판 전체 보기 →</PanelFootLink>
        </HomePanel>
      </MainGrid>
    </Wrapper>
  );
};

export default PortalHomePage;
